import { c as _c } from "react/compiler-runtime";
import { getOrgGraphsByGraphIdNodesByNodeIdContextOptions } from '@onefootprint/fetch-dashboard';
import type { NodeConfigurationContext } from '@onefootprint/request-types/dashboard';
import { useQuery } from '@tanstack/react-query';
import useSidebar from './use-sidebar';
import useStore from './use-store';
type NodeConfig<T extends NodeConfigurationContext['kind']> = Extract<NodeConfigurationContext, {
  kind: T;
}>;
const isCorrectKind = <T extends NodeConfigurationContext['kind'],>(nodeKind: T, nodeConfig: NodeConfigurationContext): nodeConfig is NodeConfig<T> => {
  return nodeConfig?.kind === nodeKind;
};

/** Fetch the node's configuration context from the backend. Returns nothing when the node's drawer is not yet open */
export const useGetNodeConfigContext = (nodeId, configKind) => {
  const $ = _c(10);
  const {
    data: t0
  } = useStore();
  const {
    graph
  } = t0;
  const {
    isOpen
  } = useSidebar();
  let t1;
  if ($[0] !== graph.id || $[1] !== nodeId) {
    t1 = getOrgGraphsByGraphIdNodesByNodeIdContextOptions({
      path: {
        graphId: graph.id,
        nodeId
      }
    });
    $[0] = graph.id;
    $[1] = nodeId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== graph.id || $[4] !== isOpen || $[5] !== nodeId) {
    t2 = !!graph.id && isOpen(nodeId);
    $[3] = graph.id;
    $[4] = isOpen;
    $[5] = nodeId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      ...t1,
      staleTime: 0,
      enabled: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const query = useQuery(t3);
  const context = query.data?.context;
  if (context && !isCorrectKind(configKind, context)) {
    throw new Error(`Received unexpected node config kind: ${context?.kind} instead of ${configKind}`);
  }
  return context;
};